import { Chip, Container, Grid, makeStyles } from "@material-ui/core";
import FiberManualRecordIcon from "@material-ui/icons/FiberManualRecord";
import { ConfirmButtonTransitionState } from "@saleor/components/ConfirmButton";
import Form from "@saleor/components/Form";
import PageBackNavigation from "@saleor/components/PageBackNavigation";
import PageBreadcrumbNavigation from "@saleor/components/PageBreadcrumbNavigation";
import PageHeader from "@saleor/components/PageHeader";
import { parseRawDraftContentState } from "@saleor/components/RichTextEditor/rawDraftContentState";
import SaveButtonBar from "@saleor/components/SaveButtonBar";
import { CouponCollectionAssignedCouponsList_couponCollection_coupons_edges_node } from "@saleor/coupons/types/CouponCollectionAssignedCouponsList";
import { CouponCollectionDetailQuery_couponCollection } from "@saleor/coupons/types/CouponCollectionDetailQuery";
import useNotifier from "@saleor/hooks/useNotifier";
import { maybe } from "@saleor/misc";
import { ListActions, ListProps } from "@saleor/types";
import { CouponCollectionStatusEnum } from "@saleor/types/globalTypes";
import { dateTimePhTimeZone } from "@saleor/utils/smop/timeZonePH";
import {
  couponCollectionDraftValidation,
  CouponCollectionErrorForm,
  CouponCollectionFormData,
  couponCollectionPublishValidation,
  hasNoError
} from "@saleor/utils/validators/couponCollection";
import React, { useState } from "react";

import CouponCollectionAssignedCoupons from "../CouponCollectionAssignedCoupons";
import CouponCollectionDeepLink from "../CouponCollectionDeepLink";
import CouponCollectionDetails from "../CouponCollectionDetails";
import CouponCollectionImage from "../CouponCollectionImage";
import CouponCollectionSchedule from "../CouponCollectionSchedule";
import CouponEditDialog from "../CouponEditDialog";
import {
  CouponStatus,
  fiberIconColors,
  statusColors
} from "../CouponRankingList/style";

const useStyles = makeStyles(
  theme => ({
    chip: {
      marginLeft: "1em"
    },
    chipLabel: {
      fontWeight: 600
    },
    container: {
      alignItems: "stretch",
      display: "flex",
      flexDirection: "row",
      justifyContent: "center",
      [theme.breakpoints.down("sm")]: {
        flexDirection: "column"
      }
    },
    couponTitleChip: {
      display: "flex"
    },
    gridItem: {
      flex: 1
    },
    pageGrid: {
      display: "grid",
      gridColumnGap: "24px",
      gridRowGap: "24px",
      gridTemplateColumns: "11fr 6fr"
    },
    pageHeader: {
      display: "flex",
      justifyContent: "space-between"
    },
    previewbutton: {
      marginBottom: "15px"
    },
    requiredSymbol: {
      color: "red"
    },
    sideGrid: {
      width: "100%"
    },
    subtitle: {
      fontSize: "1rem"
    }
  }),
  { name: "CouponCollectionEdit" }
);

export interface CouponCollectionEditProps extends ListActions, ListProps {
  listElements: string[];
  collection: CouponCollectionDetailQuery_couponCollection;
  assignedCoupons: CouponCollectionAssignedCouponsList_couponCollection_coupons_edges_node[];
  saveButtonBarState: ConfirmButtonTransitionState;
  onCouponCollectionImageUpload: (file: File) => void;
  couponCollectionImage: any;
  onBack: () => void;
  onGenerateDeeplink: (id: string) => void;
  disabled: boolean;
  onActivate: () => void;
  onDeactivate: () => void;
  onUnassign?: (string) => void;
  onDraft(data: CouponCollectionFormData);
  onPublish(data: CouponCollectionFormData);
}

export const CouponCollectionEdit: React.FC<CouponCollectionEditProps> = props => {
  const {
    onDraft,
    onPublish,
    onBack,
    onGenerateDeeplink,
    onCouponCollectionImageUpload,
    assignedCoupons,
    saveButtonBarState,
    onActivate,
    onDeactivate,
    collection,
    settings,
    onUnassign,
    ...listProps
  } = props;
  const classes = useStyles(props);
  const [chipStatus, setChipStatus] = useState(
    maybe(() => collection?.status || "")
  );
  const notify = useNotifier();
  const [editModal, setEditModal] = useState(false);
  const [validator, setValidator] = useState<CouponCollectionErrorForm>({
    title: null
  });

  if (!collection) {
    return null;
  }

  const parsedWriteup = parseRawDraftContentState(collection.writeup);
  const initialForm: CouponCollectionFormData = {
    bannerImage: maybe(() => collection?.bannerImage.url || ""),
    categories: collection?.categories,
    classification: collection?.classification,
    collectionType: collection?.collectionType,
    endDate: collection?.endDate
      ? dateTimePhTimeZone(maybe(() => collection?.endDate || "")).date
      : "",
    endTime: collection?.endDate
      ? dateTimePhTimeZone(maybe(() => collection?.endDate || "")).time
      : "",
    id: collection?.id,
    isFeatured: collection?.isFeatured,
    isHidden: collection?.isHidden,
    slug: collection?.slug || "",
    startDate: collection?.startDate
      ? dateTimePhTimeZone(maybe(() => collection?.startDate || "")).date
      : "",
    startTime: collection?.startDate
      ? dateTimePhTimeZone(maybe(() => collection?.startDate || "")).time
      : "",
    status: maybe(() => collection?.status || ""),
    title: maybe(() => collection?.title, ""),
    writeup: parsedWriteup
  };

  const breadcrumbDirectory = [
    {
      name: "Coupon Collection",
      url: "/coupons/collection"
    }
  ];

  const disableBasedOnStatus =
    collection?.status === "ACTIVE" ||
    collection?.status === "SCHEDULED" ||
    collection?.status === "EXPIRED";

  const handleValidation = data => {
    const validatePublish = couponCollectionPublishValidation(data);
    setValidator(validatePublish);
    if (validatePublish.bannerImage) {
      notify({
        status: "error",
        text: validatePublish.bannerImage
      });
    }
    return validatePublish;
  };

  const handleSubmit = event => {
    const validate = handleValidation(event);
    setValidator(validate);
    if (hasNoError(validate)) {
      onPublish(event);
    }
  };

  const handleEditModal = data => {
    const validate = handleValidation(data);
    setValidator(validate);
    if (hasNoError(validate)) {
      setEditModal(!editModal);
    }
  };

  const handleDraftSubmit = data => {
    const draftValidate = couponCollectionDraftValidation(data);
    setValidator(draftValidate);
    if (!hasNoError(draftValidate)) {
      return;
    }
    onDraft(data);
  };

  const collectionTitle = maybe(
    () => collection.title,
    "Edit Coupon Collection"
  );

  return (
    <Form initial={initialForm} onSubmit={handleSubmit} confirmLeave>
      {({ data, change, hasChanged, submit, triggerChangeFalse }) => {
        const handleDeactivate = () => {
          onDeactivate();
          triggerChangeFalse();
          onCouponCollectionImageUpload(null);
        };
        setChipStatus(collection?.status);
        return (
          <Container>
            <PageBreadcrumbNavigation items={breadcrumbDirectory} />
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                marginTop: "10px"
              }}
            >
              <PageBackNavigation onBack={onBack} />
              <PageHeader
                title={
                  <>
                    <span>{collectionTitle} </span>
                    <Chip
                      className={classes.chipLabel}
                      label={CouponStatus[chipStatus]}
                      style={{ backgroundColor: statusColors[chipStatus] }}
                      icon={
                        <FiberManualRecordIcon
                          style={{
                            color: fiberIconColors[chipStatus]
                          }}
                        />
                      }
                    />
                    <br />
                    <span className={classes.subtitle}>
                      Fields marked with (
                      <span className={classes.requiredSymbol}>*</span>) are
                      required.
                    </span>
                  </>
                }
              />
            </div>
            <Grid container className={classes.pageGrid}>
              <div>
                <CouponCollectionDetails
                  data={data}
                  onChange={change}
                  error={validator}
                  disabled={disableBasedOnStatus}
                  collection={collection}
                  mode="edit"
                />
                <CouponCollectionSchedule
                  disabled={disableBasedOnStatus}
                  data={data}
                  onChange={change}
                  localError={validator}
                />
                <CouponCollectionAssignedCoupons
                  settings={settings}
                  coupons={assignedCoupons}
                  onUnassign={onUnassign}
                  {...listProps}
                />
              </div>
              <div>
                <CouponCollectionImage
                  data={data}
                  disabled={disableBasedOnStatus}
                  onChange={change}
                  errors={validator}
                  onCouponCollectionImageUpload={onCouponCollectionImageUpload}
                />
                <CouponCollectionDeepLink
                  disabled={disableBasedOnStatus}
                  data={data}
                  onChange={change}
                  linkId={collection && collection.id}
                  linkUrl={collection && collection.deeplinkUrl}
                  shortLinkUrl={collection && collection.shortDynamicLink}
                  onGenerateDeeplink={onGenerateDeeplink}
                />
              </div>
              <CouponEditDialog
                open={editModal}
                onClose={() => setEditModal(false)}
                onSave={submit}
                collection={true}
              />
            </Grid>
            <div>
              {collection?.status === CouponCollectionStatusEnum.DRAFT ? (
                <SaveButtonBar
                  state={saveButtonBarState}
                  onCancel={onBack}
                  onPublish={submit}
                  labels={{ cancel: "CANCEL" }}
                  onDeactivate={handleDeactivate}
                  onDraft={() => handleDraftSubmit(data)}
                  hideDeactivate={false}
                  disableAlternateButton={!hasChanged}
                  saveButtonVariant={true}
                  disabled={!hasChanged}
                  hideSave={true}
                  onSave={() => onPublish(data)}
                />
              ) : collection?.status === CouponCollectionStatusEnum.INACTIVE ? (
                <SaveButtonBar
                  state={saveButtonBarState}
                  onCancel={onBack}
                  onSave={() => handleEditModal(data)}
                  onActivate={onActivate}
                  hideActivate={
                    collection?.status === CouponCollectionStatusEnum.INACTIVE
                  }
                  disabled={!hasChanged}
                  saveButtonVariant={true}
                  disableAlternateButton={!hasChanged}
                />
              ) : disableBasedOnStatus ? (
                <SaveButtonBar
                  state={saveButtonBarState}
                  onCancel={onBack}
                  labels={{ cancel: "CANCEL" }}
                  disableAlternateButton={true}
                  onDeactivate={handleDeactivate}
                  hideDeactivate={true}
                  saveButtonVariant={true}
                  disabled={true}
                  onSave={() => onPublish(data)}
                />
              ) : (
                <SaveButtonBar
                  state={saveButtonBarState}
                  onCancel={onBack}
                  labels={{ cancel: "CANCEL" }}
                  disableAlternateButton={!hasChanged}
                  onDeactivate={handleDeactivate}
                  hideDeactivate={true}
                  saveButtonVariant={true}
                  disabled={!hasChanged}
                  onSave={() => onPublish(data)}
                />
              )}
            </div>
          </Container>
        );
      }}
    </Form>
  );
};

export default CouponCollectionEdit;
